import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

import { SceneManager } from './core/SceneManager.js';
import { CameraController } from './core/CameraController.js';
import { DonutScene } from './scenes/DonutScene.js';
import { GalleryScene } from './scenes/GalleryScene.js';
import { UIController } from './ui/UIController.js';
import { DeviceDetector } from './utils/DeviceDetector.js';
import { PerformanceMonitor } from './utils/PerformanceMonitor.js';
import { SoundManager } from './utils/SoundManager.js';

gsap.registerPlugin(ScrollTrigger);

export class PortfolioApp {
    constructor(loadingManager) {
        this.loadingManager = loadingManager;
        this.canvas = document.getElementById('webgl-canvas');

        this.deviceDetector = new DeviceDetector();
        this.deviceDetector.logDeviceInfo();

        this.setupRenderer();
        this.setupCamera();
        this.setupLenis();
        this.setupAudio();

        this.sceneManager = new SceneManager(this);
        this.cameraController = new CameraController(this);
        this.uiController = new UIController(this);
        this.performanceMonitor = new PerformanceMonitor(this);
        this.soundManager = new SoundManager();

        this.bindEvents();
    }

    setupAudio() {
        // Create audio elements for each scene
        this.donutSceneMusic = new Audio('/donutscene.mp3');
        this.gallerySceneMusic = new Audio('/galleryscene.mp3');

        // Set volume (30% for background music)
        this.donutSceneMusic.volume = 0.3;
        this.gallerySceneMusic.volume = 0.3;

        // Enable looping for background music
        this.donutSceneMusic.loop = true;
        this.gallerySceneMusic.loop = true;

        // Add error handling for audio loading
        this.donutSceneMusic.addEventListener('error', (e) => {
            console.warn('Donut scene music failed to load:', e);
        });
        this.gallerySceneMusic.addEventListener('error', (e) => {
            console.warn('Gallery scene music failed to load:', e);
        });

        // Optional: Preload audio for smoother playback
        this.donutSceneMusic.preload = 'auto';
        this.gallerySceneMusic.preload = 'auto';

        // Track music state (starts as OFF, user can turn it on)
        this.isMusicEnabled = false;
    }

    // Helper method for smooth audio transitions
    fadeAudio(audio, targetVolume, duration = 1000) {
        return new Promise((resolve) => {
            const startVolume = audio.volume;
            const volumeChange = targetVolume - startVolume;
            const steps = 50; // Number of volume steps
            const stepTime = duration / steps;
            const stepChange = volumeChange / steps;
            
            let currentStep = 0;
            
            const fadeInterval = setInterval(() => {
                currentStep++;
                const newVolume = startVolume + (stepChange * currentStep);
                audio.volume = Math.max(0, Math.min(1, newVolume));
                
                if (currentStep >= steps) {
                    clearInterval(fadeInterval);
                    audio.volume = targetVolume;
                    
                    // If fading to 0, pause the audio
                    if (targetVolume === 0) {
                        audio.pause();
                        audio.currentTime = 0;
                    }
                    
                    resolve();
                }
            }, stepTime);
        });
    }

    // Helper method to start audio with fade in
    async playAudioWithFade(audio, targetVolume = 0.3, duration = 1000) {
        try {
            // Resume audio context for mobile browsers
            await this.soundManager.resumeAudioContext();

            audio.volume = 0;
            await audio.play();
            await this.fadeAudio(audio, targetVolume, duration);
        } catch (err) {
            console.warn('Audio playback failed (likely mobile autoplay restriction):', err);
            console.log('Tip: Music will play after user enables it via the music button');
        }
    }

    async init() {
        await this.loadAssets();
        
        this.donutScene = new DonutScene(this);
        this.galleryScene = new GalleryScene(this);
        
        await this.donutScene.init();
        await this.galleryScene.init();
        
        this.sceneManager.setScene('donut');
        this.uiController.showScene(1);
        
        // Debug: Check if donut and scene elements are visible after initialization
        if (this.donutScene) {
            console.log('=== DONUT SCENE DEBUG ===');
            console.log('Scene children count:', this.donutScene.scene.children.length);
            
            if (this.donutScene.donutModel) {
                console.log('Donut loaded successfully:', {
                    visible: this.donutScene.donutModel.visible,
                    position: this.donutScene.donutModel.position,
                    scale: this.donutScene.donutModel.scale
                });
            } else {
                console.warn('Donut not loaded properly');
            }
            
            // Check all scene objects
            this.donutScene.scene.traverse((child) => {
                if (child.isMesh) {
                    console.log('Mesh found:', {
                        name: child.name || 'unnamed',
                        visible: child.visible,
                        position: child.position,
                        scale: child.scale,
                        material: child.material?.type
                    });
                }
            });
            
            console.log('Camera position:', this.camera.position);
            console.log('Camera looking at direction:', {
                x: this.camera.getWorldDirection(new THREE.Vector3()).x,
                y: this.camera.getWorldDirection(new THREE.Vector3()).y,
                z: this.camera.getWorldDirection(new THREE.Vector3()).z
            });
            console.log('=== END DEBUG ===');
        }
        
        this.startRenderLoop();

        // Final debug check
        setTimeout(() => {
            console.log('=== FINAL SCENE CHECK ===');
            console.log('Current scene name:', this.sceneManager.getCurrentSceneName());
            console.log('Current scene children:', this.sceneManager.currentScene?.children.length);
            console.log('Canvas visible:', this.canvas.style.display !== 'none');
            console.log('Canvas z-index:', getComputedStyle(this.canvas).zIndex);
            console.log('=== END FINAL CHECK ===');
        }, 1000);

        this.loadingManager.setProgress(1);
    }

    setupRenderer() {
        const quality = this.deviceDetector.getQualitySettings();
        
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: quality.antialiasing,
            alpha: true,
            premultipliedAlpha: false,
            powerPreference: this.deviceDetector.isMobile ? "default" : "high-performance"
        });
        
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(this.deviceDetector.capabilities.pixelRatio);
        this.renderer.setClearColor(0x000000, 0); // Transparent background
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1;
        
        if (quality.shadowMapSize > 0) {
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = this.deviceDetector.isLowPerformance ? 
                THREE.BasicShadowMap : THREE.PCFSoftShadowMap;
        }
    }

    setupCamera() {
        // Use responsive FOV based on device size
        const fov = this.deviceDetector.getResponsiveFOV();

        this.camera = new THREE.PerspectiveCamera(
            fov,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );

        // Adjust camera to better view donut and table
        // Apply distance multiplier for smaller screens
        const distMultiplier = this.deviceDetector.getCameraDistanceMultiplier();
        this.camera.position.set(-1 * distMultiplier, 2 * distMultiplier, 2 * distMultiplier);
        this.camera.lookAt(-3.7, 0, -3);
    }

    setupLenis() {
        this.lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smooth: true,
            mouseMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
        });

        const raf = (time) => {
            // Only run Lenis when NOT in gallery mode to prevent conflicts
            if (!this.isInGallery || !this.isInGallery()) {
                this.lenis.raf(time);
            }
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);
    }

    async loadAssets() {
        const loader = new GLTFLoader(this.loadingManager.manager);

        try {
            const donutGLTF = await loader.loadAsync('/donut.glb');
            const boothGLTF = await loader.loadAsync('/booth.glb');
            this.assets = {
                donut: donutGLTF,
                booth: boothGLTF
            };

            this.loadingManager.setProgress(0.8);
        } catch (error) {
            console.error('Error loading assets:', error);
            throw error;
        }
    }


    resetCamera() {
        this.cameraController.reset();
    }

    bindEvents() {
        window.addEventListener('resize', this.onWindowResize.bind(this));
        
        this.canvas.addEventListener('click', async (event) => {
            console.log('Canvas clicked');
            if (this.donutScene) {
                const donutClicked = this.donutScene.checkDonutClick(event);
                console.log('Donut clicked:', donutClicked);
                if (donutClicked) {
                    console.log('Triggering character animation');

                    // Resume audio context for mobile (unlock audio on first interaction)
                    await this.soundManager.resumeAudioContext();

                    this.soundManager.playDonutClick();
                    this.donutScene.triggerCharacterPopOut();
                }
            }
        });

        // Back to donut button
        document.getElementById('back-to-donut')?.addEventListener('click', () => {
            this.transitionBackToDonut();
        });

        // Gallery scroll navigation
        let isInGallery = false;
        let currentPedestalIndex = 0;
        const totalPedestals = 5;
        let lastScrollTime = 0;
        const scrollThrottle = 350; // Minimum time between transitions in ms
        let isNavigating = false; // Flag to prevent rapid navigation
        let lastWheelEventTime = 0; // Track individual wheel events for debouncing
        const debounceWindow = 100; // ms - wait for scroll to settle before processing

        // Track when we're in gallery mode
        this.isInGallery = () => isInGallery;
        this.setGalleryMode = (enabled) => { isInGallery = enabled; };

        // Desktop: Scroll wheel navigation (up/down only - no left/right)
        // NOTE: Scrollable elements (floating-writeup, chatbox) handle their own wheel events
        // with stopPropagation, so any events reaching here are guaranteed to be on pedestal area
        window.addEventListener('wheel', (event) => {
            if (!isInGallery || !this.galleryScene || !this.galleryScene.isZoomedIn) return;

            // Filter out horizontal scrolls - only process vertical scrolls
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                return; // Primarily horizontal scroll - ignore
            }

            // Filter out momentum scrolling events (small deltaY values)
            if (Math.abs(event.deltaY) < 40) {
                return; // Momentum scroll - ignore
            }

            // Prevent default scroll behavior for pedestal area
            event.preventDefault();

            // Update timestamp BEFORE any checks so blocked events still update it
            const now = Date.now();
            const timeSinceLastWheel = now - lastWheelEventTime;
            lastWheelEventTime = now; // Update for every event to maintain proper debounce

            // Block ALL events if currently navigating - prevents ANY navigation during transition
            if (isNavigating) {
                return;
            }

            // Reject if within debounce window
            if (timeSinceLastWheel < debounceWindow) {
                return; // Too soon since last wheel event
            }

            // Throttle based on time - prevent navigation if too soon after last one
            if (now - lastScrollTime < scrollThrottle) {
                return;
            }

            // Set flags IMMEDIATELY before any async operations
            isNavigating = true;
            lastScrollTime = now;

            // Only navigate on vertical scroll (ignore horizontal deltaX)
            const scrollDelta = event.deltaY > 0 ? 1 : -1;

            if (scrollDelta > 0) {
                // Scroll down - next pedestal (wrap to first if at end)
                currentPedestalIndex = (currentPedestalIndex + 1) % totalPedestals;
                this.navigateToPedestal(currentPedestalIndex);
            } else if (scrollDelta < 0) {
                // Scroll up - previous pedestal (wrap to last if at beginning)
                currentPedestalIndex = (currentPedestalIndex - 1 + totalPedestals) % totalPedestals;
                this.navigateToPedestal(currentPedestalIndex);
            }

            // Reset navigation flag after throttle period
            setTimeout(() => { isNavigating = false; }, scrollThrottle);
        }, { passive: false });

        // Mobile: Touch swipe navigation
        let touchStartX = 0;
        let touchEndX = 0;
        const swipeThreshold = 50;

        let touchStartY = 0;
        let isTouchInsideScrollable = false;

        window.addEventListener('touchstart', (event) => {
            if (!isInGallery || !this.galleryScene || !this.galleryScene.isZoomedIn) return;

            // Check if touching inside text panel or chatbox by traversing parent elements
            let targetElement = event.target;
            isTouchInsideScrollable = false;
            while (targetElement && targetElement !== document.body) {
                if (targetElement.id === 'floating-writeup' ||
                    targetElement.id === 'gallery-chatbox' ||
                    targetElement.id === 'chatbox-backdrop') {
                    // Mark that touch is inside scrollable element - don't capture gesture data
                    isTouchInsideScrollable = true;
                    return;
                }
                targetElement = targetElement.parentElement;
            }

            // Only capture touch coordinates if NOT inside scrollable elements
            touchStartX = event.touches[0].clientX;
            touchStartY = event.touches[0].clientY;
        }, { passive: true });

        window.addEventListener('touchend', (event) => {
            if (!isInGallery || !this.galleryScene || !this.galleryScene.isZoomedIn) return;
            if (isNavigating) return; // Prevent rapid navigation

            // If touch started inside scrollable element, don't process as navigation gesture
            if (isTouchInsideScrollable) {
                isTouchInsideScrollable = false; // Reset for next gesture
                return;
            }

            // Check if touch ended on chatbox or text panel
            let targetElement = event.target;
            while (targetElement && targetElement !== document.body) {
                if (targetElement.id === 'floating-writeup' ||
                    targetElement.id === 'gallery-chatbox' ||
                    targetElement.id === 'chatbox-backdrop') {
                    return;
                }
                targetElement = targetElement.parentElement;
            }

            touchEndX = event.changedTouches[0].clientX;
            const touchEndY = event.changedTouches[0].clientY;

            const swipeDistanceX = touchEndX - touchStartX;
            const swipeDistanceY = Math.abs(touchEndY - touchStartY);

            // Only trigger navigation if horizontal swipe is dominant (not vertical scroll)
            if (Math.abs(swipeDistanceX) < swipeThreshold || swipeDistanceY > Math.abs(swipeDistanceX)) return;

            const now = Date.now();
            if (now - lastScrollTime < scrollThrottle) return;

            isNavigating = true;
            lastScrollTime = now;

            if (swipeDistanceX > 0) {
                // Swiped right - previous project (wrap to last if at beginning)
                currentPedestalIndex = (currentPedestalIndex - 1 + totalPedestals) % totalPedestals;
                this.navigateToPedestal(currentPedestalIndex);
            } else if (swipeDistanceX < 0) {
                // Swiped left - next project (wrap to first if at end)
                currentPedestalIndex = (currentPedestalIndex + 1) % totalPedestals;
                this.navigateToPedestal(currentPedestalIndex);
            }

            // Reset navigation flag after throttle period
            setTimeout(() => { isNavigating = false; }, scrollThrottle);
        }, { passive: true });

        // Arrow key navigation removed - desktop uses only scroll wheel for navigation
        
        // Progress dot navigation
        document.querySelectorAll('.progress-dot').forEach((dot, index) => {
            dot.addEventListener('click', () => {
                if (isInGallery) {
                    currentPedestalIndex = index;
                    this.switchToPedestal(index);
                    
                    // If zoomed in, automatically zoom to the new pedestal
                    if (this.galleryScene && this.galleryScene.isZoomedIn) {
                        setTimeout(() => {
                            this.galleryScene.zoomInToPedestal(index);
                        }, 400); // Wait for transition to complete
                    }
                }
            });
        });
        
        // Add method to update current pedestal index
        this.updateCurrentPedestalIndex = (index) => {
            currentPedestalIndex = index;
        };

        // Music toggle button
        document.getElementById('music-toggle')?.addEventListener('click', () => {
            this.toggleMusic();
        });

        // Skip intro button
        document.getElementById('skip-intro')?.addEventListener('click', () => {
            this.skipIntro();
        });
    }

    skipIntro() {
        console.log('Skipping intro');
        // Stop dialogue sequence
        if (this.donutScene && this.donutScene.stopDialogue) {
            this.donutScene.stopDialogue();
        }
        // Hide character if visible
        if (this.donutScene && this.donutScene.character) {
            this.donutScene.character.visible = false;
        }
        // Transition directly to gallery
        this.transitionToGallery();
    }

    navigateToPedestal(index) {
        // Smoothly slide camera to the new pedestal
        if (this.galleryScene) {
            this.galleryScene.slideTowardsPedestal(index);
            // Sync the zoomed pedestal state
            this.galleryScene.zoomedPedestal = index;
        }

        this.updateProjectInfo(index);

        // Update progress dots
        document.querySelectorAll('.progress-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
    }
    
    switchToPedestal(index) {
        // Use smooth transition for better museum experience
        if (this.galleryScene) {
            if (this.galleryScene.smoothTransitionToPedestal) {
                this.galleryScene.smoothTransitionToPedestal(index);
            } else {
                this.galleryScene.showPedestal(index);
            }
        }
        
        // Keep camera in front center position
        if (this.cameraController) {
            this.cameraController.moveTo({ x: 0, y: 3, z: 2 }, 0.8);
        }
        
        // Update progress dots
        document.querySelectorAll('.progress-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
        
        // Update project info
        this.updateProjectInfo(index);
    }
    
    updateProjectInfo(index) {
        // Get project data from the gallery scene
        if (this.galleryScene && this.galleryScene.projectModels[index]) {
            const projectData = this.galleryScene.projectModels[index].data;

            const titleElement = document.getElementById('current-project-title');
            const descriptionElement = document.getElementById('current-project-description');
            const technologiesElement = document.getElementById('current-project-technologies');
            const yearElement = document.getElementById('current-project-year');
            const categoryElement = document.getElementById('current-project-category');

            if (titleElement) titleElement.textContent = projectData.title;
            if (descriptionElement) descriptionElement.textContent = projectData.description;
            if (technologiesElement) technologiesElement.textContent = `Technologies: ${projectData.technologies}`;
            if (yearElement) yearElement.textContent = `Year: ${projectData.year}`;
            if (categoryElement) categoryElement.textContent = projectData.category;
        }
    }

    async toggleMusic() {
        const musicIcon = document.getElementById('music-icon');

        if (this.isMusicEnabled) {
            // Fade out and stop all music
            this.isMusicEnabled = false;
            if (musicIcon) musicIcon.src = '/music-off.png';

            // Mute sound effects
            this.soundManager.setMuted(true);

            await Promise.all([
                this.fadeAudio(this.donutSceneMusic, 0, 500),
                this.fadeAudio(this.gallerySceneMusic, 0, 500)
            ]);
        } else {
            // Resume music based on current scene with fade in
            this.isMusicEnabled = true;
            if (musicIcon) musicIcon.src = '/music-player.png';

            // Enable sound effects
            this.soundManager.setMuted(false);

            const currentSceneName = this.sceneManager.getCurrentSceneName();
            if (currentSceneName === 'donut') {
                await this.playAudioWithFade(this.donutSceneMusic, 0.3, 500);
            } else if (currentSceneName === 'gallery') {
                await this.playAudioWithFade(this.gallerySceneMusic, 0.3, 500);
            }
        }
    }

    transitionToGallery() {
        console.log('Transitioning to 3D portfolio gallery');

        // Play portal transition sound
        this.soundManager.playPortalSound();

        const galleryState = document.getElementById('gallery-state');
        const scene1 = document.getElementById('scene1');

        // Switch to 3D gallery scene
        this.sceneManager.setScene('gallery');

        // Switch music from donut to gallery with smooth transition
        if (this.isMusicEnabled) {
            this.fadeAudio(this.donutSceneMusic, 0, 300).then(() => {
                this.playAudioWithFade(this.gallerySceneMusic, 0.3, 500);
            });
        }

        // Enable gallery mode for scroll navigation
        this.setGalleryMode(true);

        // Hide gallery background image when entering gallery scene
        const galleryBackground = document.querySelector('.gallery-background');
        if (galleryBackground) {
            galleryBackground.style.display = 'none';
        }

        // Hide skip intro button when entering gallery
        const skipButton = document.getElementById('skip-intro');
        if (skipButton) {
            skipButton.style.display = 'none';
        }

        // Hide DonutScene character when entering gallery
        if (this.donutScene && this.donutScene.characterContainer) {
            this.donutScene.characterContainer.style.display = 'none';
            this.donutScene.characterContainer.style.opacity = '0';
            this.donutScene.characterContainer.style.pointerEvents = 'none';
        }

        // Update camera controller defaults for gallery view
        if (this.cameraController) {
            this.cameraController.setDefaultPosition(
                { x: 0, y: 5, z: 12 },  // Gallery overview position - elevated for full view
                { x: 0, y: 1, z: -5 }   // Look at gallery center
            );
        }

        // Show gallery scene elements
        if (this.galleryScene) {
            this.galleryScene.show();
        }

        if (galleryState && scene1) {
            // Hide the donut scene UI
            scene1.classList.remove('active');

            // Show the gallery UI overlay
            galleryState.classList.add('active');
        }

        // Set camera to overview position
        this.camera.position.set(0, 5, 12);
        this.camera.lookAt(0, 1, -5);

        // Initialize progress dots (first one active by default)
        document.querySelectorAll('.progress-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === 0);
        });
    }

    transitionBackToDonut() {
        console.log('Transitioning back to donut scene');

        // Play portal transition sound
        this.soundManager.playPortalSound();

        const galleryState = document.getElementById('gallery-state');
        const scene1 = document.getElementById('scene1');

        // Switch music from gallery to donut with smooth transition
        if (this.isMusicEnabled) {
            this.fadeAudio(this.gallerySceneMusic, 0, 300).then(() => {
                this.playAudioWithFade(this.donutSceneMusic, 0.3, 500);
            });
        }

        // Disable gallery mode
        this.setGalleryMode(false);

        // Show gallery background image when returning to donut scene
        const galleryBackground = document.querySelector('.gallery-background');
        if (galleryBackground) {
            galleryBackground.style.display = 'block';
        }

        // Hide GalleryScene character when returning to donut
        if (this.galleryScene && this.galleryScene.characterContainer) {
            this.galleryScene.characterContainer.style.opacity = '0';
            this.galleryScene.characterContainer.style.pointerEvents = 'none';
        }

        // Hide all gallery scene elements to prevent interaction
        if (this.galleryScene) {
            this.galleryScene.hide();
        }

        // Switch back to donut scene
        this.sceneManager.setScene('donut');
        
        // Ensure donut scene elements are visible
        if (this.donutScene) {
            if (this.donutScene.donutModel) {
                this.donutScene.donutModel.visible = true;
                console.log('Donut visibility restored:', this.donutScene.donutModel.visible);
                console.log('Donut position:', this.donutScene.donutModel.position);
                console.log('Donut scale:', this.donutScene.donutModel.scale);
            }

            // Restore DonutScene character
            if (this.donutScene.characterContainer) {
                this.donutScene.characterContainer.style.display = 'block';
            }

            // Make sure all scene elements are visible
            this.donutScene.scene.traverse((child) => {
                if (child.isMesh) {
                    child.visible = true;
                }
            });
        }
        
        // Restore original camera controller defaults for donut scene
        if (this.cameraController) {
            this.cameraController.setDefaultPosition(
                { x: -1, y: 1, z: 3 },  // Original donut position
                { x: -4, y: 0, z: -3 }  // Original donut target
            );
        }
        
        if (galleryState && scene1) {
            // Hide the gallery
            galleryState.classList.remove('active');
            
            // Show the donut scene UI
            scene1.classList.add('active');
        }
    }

    onWindowResize() {
        // Update camera aspect ratio
        this.camera.aspect = window.innerWidth / window.innerHeight;

        // Update FOV based on new screen size
        const newFOV = this.deviceDetector.getResponsiveFOV();
        if (this.camera.fov !== newFOV) {
            this.camera.fov = newFOV;
        }

        this.camera.updateProjectionMatrix();

        // Update renderer size
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        // Update quality settings based on new screen size
        const quality = this.deviceDetector.getQualitySettings();
        this.renderer.setPixelRatio(quality.pixelRatio);

        // Notify scenes of resize
        this.donutScene?.onResize();
        this.galleryScene?.onResize();

        // Log device info on significant changes (orientation change)
        const newOrientation = this.deviceDetector.detectOrientation();
        if (newOrientation !== this.deviceDetector.orientation) {
            console.log('Orientation changed to:', newOrientation);
            this.deviceDetector.logDeviceInfo();
        }
    }

    startRenderLoop() {
        const animate = () => {
            requestAnimationFrame(animate);
            
            if (this.performanceMonitor) this.performanceMonitor.update();
            if (this.cameraController) this.cameraController.update();
            if (this.donutScene) this.donutScene.update();
            if (this.galleryScene) this.galleryScene.update();
            
            // Render the scene
            
            this.renderer.render(this.sceneManager.currentScene, this.camera);
        };
        
        animate();
    }

    dispose() {
        this.renderer.dispose();
        this.lenis.destroy();
        this.donutScene?.dispose();
    }
}